import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { ShareButtons } from '@/components/ShareButtons';
import SEO from '@/components/SEO';
import { getNewsBySlug } from '@/lib/api';

const SITE_URL = 'https://paisleyscotland.com';

const NewsDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getNewsBySlug(slug)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Article not found</h1>
          <Link to="/news" className="text-sky-600 hover:underline">← Back to news</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-32">Loading…</div>
        <Footer />
      </div>
    );
  }

  const canonical = `${SITE_URL}/news/${post.slug}`;

  return (
    <div className="min-h-screen bg-white" data-testid="news-detail-page">
      <SEO
        title={`${post.title} | Paisley Scotland`}
        description={post.meta_description || post.excerpt || ''}
        keywords={(post.tags || []).join(', ')}
        url={canonical}
        type="article"
        image={post.cover_image || undefined}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.meta_description || post.excerpt,
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          author: { '@type': 'Organization', name: post.author || 'Paisley Scotland' },
          publisher: { '@type': 'Organization', name: 'Paisley Scotland' },
          mainEntityOfPage: canonical,
          image: post.cover_image || undefined,
        }}
      />
      <Navbar />

      <article className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/news" className="inline-flex items-center text-sm text-slate-500 hover:text-sky-600 mb-6">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> All articles
          </Link>

          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mb-4">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {new Date(post.created_at).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'long', year: 'numeric'
              })}
            </span>
            {post.author && <span>by {post.author}</span>}
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-8">{post.excerpt}</p>
          )}

          {post.cover_image && (
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full rounded-xl mb-10 shadow-lg"
            />
          )}

          <div
            className="prose prose-slate prose-lg max-w-none prose-headings:font-heading prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-a:text-sky-600 prose-strong:text-slate-900"
            dangerouslySetInnerHTML={{ __html: post.content }}
            data-testid="news-content"
          />

          {post.tags && post.tags.length > 0 && (
            <div className="mt-10 pt-8 border-t border-slate-200 flex flex-wrap items-center gap-2">
              <Tag className="h-4 w-4 text-slate-400" />
              {post.tags.map((t) => (
                <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
              ))}
            </div>
          )}

          <div className="mt-10">
            <ShareButtons url={canonical} title={post.title} description={post.excerpt} />
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default NewsDetailPage;
